// A local game: the bot or a second person on the same screen. Everything is
// decided here in the browser, so a restart is just a remount.
import { t } from '../i18n/I18n.js';
import type { BotPlayer } from '../ai/BotPlayer.js';
import { PRESET_MS } from '../game/clock.js';
import { DEFAULT_VARIANT } from '../game/rules/index.js';
import type { GameState } from '../game/GameState.js';
import type { Color } from '../game/types.js';
import { LocalGameLog } from '../history/LocalGameLog.js';
import { commitRecord } from '../history/serverSync.js';
import type { GameRecord } from '../history/types.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { LocalOpponent } from '../ui/types.js';
import { GameOverHost } from './GameOverHost.js';
import { LocalMatch } from './LocalMatch.js';
import type { ModeHooks, SceneMode } from './SceneMode.js';
import { offerDraw } from './drawOffer.js';

export interface LocalModeDeps {
  readonly opponent: LocalOpponent;
  /** An imported position to play from; null for the standard start. */
  readonly start: GameState | null;
  readonly settings: SettingsManager;
  readonly bot: BotPlayer;
  readonly hooks: ModeHooks;
  readonly playAgain: () => void;
  readonly exitToMenu: () => void;
}

function pickColor(choice: 'white' | 'black' | 'random'): Color {
  if (choice === 'random') return Math.random() < 0.5 ? 'w' : 'b';
  return choice === 'white' ? 'w' : 'b';
}

export function createLocalMode(deps: LocalModeDeps): SceneMode {
  const s = deps.settings.get();
  const vsBot = deps.opponent === 'bot';
  const humanColor = pickColor(s.defaultColor);
  /** The record of this game once it is over: the result dialog exports it. */
  let finished: GameRecord | null = null;
  // A side with no name of its own (the bot, both hot-seat players) is labelled when shown.
  const nameOf = (color: Color): string => (vsBot && color === humanColor ? s.playerName : '');
  const log = new LocalGameLog({
    mode: vsBot ? 'bot' : 'local',
    variant: DEFAULT_VARIANT,
    difficulty: vsBot ? s.difficulty : null,
    playerWhite: nameOf('w'),
    playerBlack: nameOf('b'),
    localColor: vsBot ? humanColor : null,
    roomCode: null,
  }, (record) => {
    finished = record;
    commitRecord(record);
  });
  const match: LocalMatch = new LocalMatch({
    opponent: deps.opponent,
    humanColor,
    ...(deps.start === null ? {} : { start: deps.start }),
    difficulty: s.difficulty,
    clockMs: s.clockEnabled ? PRESET_MS[s.clockPreset] : null,
    think: (fen, difficulty, budgetMs) => deps.bot.think(fen, difficulty, budgetMs),
    onMove: (played) => {
      log.moved(match.state, match.clock());
      deps.hooks.onMove(played);
    },
    // Resignation, an agreed draw and a flag end the game without a move.
    onChange: () => {
      log.changed(match.state);
      deps.hooks.onChange();
    },
  });
  const gameOver = new GameOverHost({ playAgain: deps.playAgain, menu: deps.exitToMenu });
  const perspective = (): Color | null => (vsBot ? match.humanColor : null);

  return {
    match,
    online: false,
    perspective,
    rotation: (turn) => {
      if (vsBot) return match.humanColor === 'b' ? 180 : 0;
      return deps.settings.get().flipBoard && turn === 'b' ? 180 : 0;
    },
    tick: (modalOpen) => {
      // Any modal over the board (pause, promotion, draw offer) stops a local clock.
      match.setPaused(modalOpen);
      match.checkFlag();
    },
    banner: () => null,
    drawButton: () => ({ label: t('game.draw.offer'), enabled: match.canHumanMove() }),
    offerDraw: () => offerDraw(match, deps.settings.get().difficulty),
    showGameOver: (outcome) => gameOver.show({ outcome, perspective: perspective(), record: finished }),
    requestRestart: () => {},
    restartState: () => ({ pending: false, canPropose: true }),
    dispose: () => {
      match.dispose();
      gameOver.close();
    },
  };
}
